import React, { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router';
import { AgGridReact } from 'ag-grid-react';
import AppContext from 'AppContext';
import GrpButton from 'Library/GrpButton/GrpButton';
import GrpSpinner from 'Components/GrpSpinner/GrpSpinner';
import {
  getScopeById,
  createScope,
  updateScope,
  getScopeFundClasses,
  createScopeFundClass,
  deleteScopeFundClass,
  getFundsAndClasses,
} from 'Services/distributionApi';
import classes from './ScopePage.module.scss';

const PAGE_SIZE = 50;

const ScopePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const context = useContext(AppContext);
  const gridRef = useRef(null);

  const isEditMode = !!id;

  const [loading, setLoading] = useState(true);
  const [scopeName, setScopeName] = useState('');
  const [isRestricted, setIsRestricted] = useState(false);
  const [allFunds, setAllFunds] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [existingFundClassIds, setExistingFundClassIds] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [selectAllChecked, setSelectAllChecked] = useState(false);

  // Build fund→classes tree from flat data
  const fundsTree = useMemo(() => {
    const map = new Map();
    allFunds.forEach((item) => {// Debug log to check the structure of each item
      if (!map.has(item.FundCode)) {
        map.set(item.FundCode, { FundCode: item.FundCode, FundName: item.FundName, classes: [] });
      }
      if (item.Classes.length > 0) {
        map.get(item.FundCode).classes = item.Classes.map((cls) => ({
          FundCode: item.FundCode,
          FundName: item.FundName,
          ClassCode: cls.ClassCode,
          ClassName: cls.ClassName,
        }));
      }
    });
    return Array.from(map.values());
  }, [allFunds]);

  // Filtered funds by search
  const filteredFunds = useMemo(() => {
    if (!searchText.trim()) return fundsTree;
    const q = searchText.toLowerCase();
    return fundsTree
      .map((fund) => {
        const fundMatch = fund.FundCode.toLowerCase().includes(q) || fund.FundName?.toLowerCase().includes(q);
        const matchedClasses = fund.classes.filter(
          (c) => c.ClassCode.toLowerCase().includes(q) || c.ClassName?.toLowerCase().includes(q),
        );
        if (fundMatch) return fund;
        if (matchedClasses.length > 0) return { ...fund, classes: matchedClasses };
        return null;
      })
      .filter(Boolean);
  }, [fundsTree, searchText]);

  const visibleFunds = useMemo(() => filteredFunds.slice(0, visibleCount), [filteredFunds, visibleCount]);

  const isSelected = useCallback(
    (fundCode, classCode) => selectedItems.some((s) => s.FundCode === fundCode && s.ClassCode === classCode),
    [selectedItems],
  );

  const isFundFullySelected = useCallback(
    (fund) => fund.classes.length > 0 && fund.classes.every((c) => isSelected(fund.FundCode, c.ClassCode)),
    [isSelected],
  );

  const toggleClass = useCallback((item) => {
    setSelectedItems((prev) => {
      const exists = prev.some((s) => s.FundCode === item.FundCode && s.ClassCode === item.ClassCode);
      if (exists) return prev.filter((s) => !(s.FundCode === item.FundCode && s.ClassCode === item.ClassCode));
      return [...prev, item];
    });
  }, []);

  const toggleFund = useCallback(
    (fund) => {
      const allSelected = isFundFullySelected(fund);
      setSelectedItems((prev) => {
        const without = prev.filter((s) => s.FundCode !== fund.FundCode);
        if (allSelected) return without;
        const newItems = fund.classes.filter(
          (c) => !without.some((s) => s.FundCode === c.FundCode && s.ClassCode === c.ClassCode),
        );
        return [...without, ...newItems];
      });
    },
    [isFundFullySelected],
  );

  const handleSelectAll = useCallback(() => {
    if (selectAllChecked) {
      setSelectedItems([]);
      setSelectAllChecked(false);
    } else {
      const allClasses = filteredFunds.flatMap((f) => f.classes);
      setSelectedItems(allClasses);
      setSelectAllChecked(true);
    }
  }, [selectAllChecked, filteredFunds]);

  const removeSelected = useCallback((item) => {
    setSelectedItems((prev) => prev.filter((s) => !(s.FundCode === item.FundCode && s.ClassCode === item.ClassCode)));
  }, []);

  // Load data
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const fundsResult = await getFundsAndClasses();
        const funds = fundsResult.value || fundsResult.Content || [];
        setAllFunds(funds);

        if (isEditMode) {
          const [scopeResult, fundClassResult] = await Promise.all([
            getScopeById(Number(id)),
            getScopeFundClasses(Number(id)),
          ]);
          const scope = scopeResult.value?.[0] || scopeResult.Content?.[0] || scopeResult;
          setScopeName(scope.ScopeName || '');
          setIsRestricted(scope.IsRestricted || false);

          const existing = fundClassResult.value || fundClassResult.Content || [];
          setExistingFundClassIds(existing);

          // Pre-select items from existing scope fund classes
          const preSelected = existing.map((e) => ({
            FundCode: e.FundCode,
            ClassCode: e.ClassCode,
            ClassName: e.ClassName || '',
            FundName: e.FundName || '',
            ScopeFundClassId: e.ScopeFundClassId,
          }));
          setSelectedItems(preSelected);
        }
      } catch (err) {
        context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to load scope data'}</> });
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isEditMode, context.handlers]);

  const handleSave = async () => {
    if (!scopeName.trim()) {
      context.handlers.setToast({ type: 'error', body: <>Scope name is required</> });
      return;
    }

    try {
      setLoading(true);
      let scopeId;

      if (isEditMode) {
        await updateScope({ ScopeId: Number(id), ScopeName: scopeName, IsRestricted: isRestricted });
        scopeId = Number(id);
      } else {
        const result = await createScope({ ScopeName: scopeName, IsRestricted: isRestricted });
        scopeId = result.value?.[0]?.ScopeId || result.Content?.[0]?.ScopeId || result.ScopeId;
      }

      // Diff existing vs selected
      if (isEditMode) {
        // Delete removed items
        const removedItems = existingFundClassIds.filter(
          (e) => !selectedItems.some((s) => s.FundCode === e.FundCode && s.ClassCode === e.ClassCode),
        );
        for (const item of removedItems) {
          await deleteScopeFundClass(item.ScopeFundClassId);
        }

        // Create new items
        const newItems = selectedItems.filter(
          (s) => !existingFundClassIds.some((e) => e.FundCode === s.FundCode && e.ClassCode === s.ClassCode),
        );
        for (const item of newItems) {
          await createScopeFundClass({
            ScopeId: scopeId,
            FundCode: item.FundCode,
            ClassCode: item.ClassCode,
          });
        }
      } else {
        // Create all for new scope
        for (const item of selectedItems) {
          await createScopeFundClass({
            ScopeId: scopeId,
            FundCode: item.FundCode,
            ClassCode: item.ClassCode,
          });
        }
      }

      context.handlers.setToast({ type: 'success', body: <>Scope saved successfully</> });
      navigate('/distribution/scopes-list');
    } catch (err) {
      context.handlers.setToast({ type: 'error', body: <>{err.human || 'Failed to save scope'}</> });
    } finally {
      setLoading(false);
    }
  };

  const selectedColumnDefs = [
    { headerName: 'Fund Code', field: 'FundCode', flex: 1, filter: 'agTextColumnFilter' },
    { headerName: 'Class Code', field: 'ClassCode', flex: 1, filter: 'agTextColumnFilter' },
    {
      headerName: '',
      width: 60,
      sortable: false,
      filter: false,
      cellRenderer: (params) => (
        <GrpButton size="Small" icon={['fas', 'trash']} iconSide="Round" onClick={() => removeSelected(params.data)} />
      ),
    },
  ];

  if (loading) return <GrpSpinner />;

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.headerLeft}>
          <input
            className={classes.scopeNameInput}
            placeholder="Scope Name"
            value={scopeName}
            onChange={(e) => setScopeName(e.target.value)}
          />
          <label className={classes.restrictedLabel}>
            <input
              type="checkbox"
              className={classes.checkbox}
              checked={isRestricted}
              onChange={(e) => setIsRestricted(e.target.checked)}
            />
            Is Restricted
          </label>
          <label className={classes.restrictedLabel}>
              <input type="checkbox" className={classes.checkbox} checked={selectAllChecked} onChange={handleSelectAll} />
                Select All Available Funds
          </label>
        </div>
        <div className={classes.headerActions}>
          <GrpButton size="Small" type="Primary" text="Save" onClick={handleSave} />
          <GrpButton size="Small" type="Secondary" text="Cancel" onClick={() => navigate('/distribution/scopes-list')} />
        </div>
      </div>

      <div className={classes.panels}>
        {/* Left panel — Available Funds / Classes */}
        <div className={classes.panel}>
          <div className={classes.panelHeader}>
            <span>Available Funds &amp; Classes</span>
            <div className={classes.panelHeaderActions}>
              <input
                className={classes.searchInput}
                style={{width:"280px"}}
                placeholder="Search by Fund Name, Fund Code or ISIN"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                  setVisibleCount(PAGE_SIZE);
                }}
              />
            </div>
          </div>
          <div className={classes.treeContainer}>
            {visibleFunds.map((fund) => (
              <div key={fund.FundCode} className={classes.fundNode}>
                <div className={classes.fundRow} onClick={() => toggleFund(fund)}>
                  <input
                    type="checkbox"
                    className={classes.checkbox}
                    checked={isFundFullySelected(fund)}
                    readOnly
                  />
                  <span>{fund.FundCode} — {fund.FundName}</span>
                </div>
                {fund.classes.map((cls) => (
                  <div key={`${cls.FundCode}-${cls.ClassCode}`} className={classes.classRow} onClick={() => toggleClass(cls)}>
                    <input
                      type="checkbox"
                      className={classes.checkbox}
                      checked={isSelected(cls.FundCode, cls.ClassCode)}
                      readOnly
                    />
                    <span>{cls.ClassCode} — {cls.ClassName}</span>
                  </div>
                ))}
              </div>
            ))}
            {visibleCount < filteredFunds.length && (
              <div className={classes.loadMore}>
                <button className={classes.loadMoreBtn} onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}>
                  Load more ({filteredFunds.length - visibleCount} remaining)
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right panel — Selected Items grid */}
        <div className={classes.panel}>
          <div className={classes.panelHeader}>
            <span>Selected ({selectedItems.length})</span>
          </div>
          <div className={`ag-theme-balham ${classes.gridContainer}`}>
            <AgGridReact
              ref={gridRef}
              rowData={selectedItems}
              columnDefs={selectedColumnDefs}
              domLayout="normal"
              defaultColDef={{ sortable: true, resizable: true }}
              getRowId={(params) => `${params.data.FundCode}-${params.data.ClassCode}`}
              suppressRowClickSelection
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScopePage;
